/**
 * RTWO Event Bus — Delivery Engine Service
 */

import { EVENT_BUS_SERVICE_IDS } from '../domain-models';
import type { DeliveryId, EventId, RTWOEventDelivery, SubscriberId } from '../domain-models';
import type { DeliveryResult, IDeliveryEngineService } from '../service-interfaces';
import type { EBusRepositories } from '../repositories/ebus-repositories';
import type { EngineSourceAdapterRegistry } from '../registry/adapter-registries';
import { EBusInternalEventTypes } from '../events/ebus-internal-event-bus';
import { BaseEBusService, type EBusServiceContext } from './base-ebus-service';

export class DeliveryEngineService extends BaseEBusService implements IDeliveryEngineService {
  readonly serviceId = EVENT_BUS_SERVICE_IDS.DELIVERY_ENGINE;
  readonly serviceName = 'Delivery Engine';
  readonly description =
    'Delivers events to subscriber modules. No deliveries until real event sources connect.';
  readonly integrations = ['Routing Engine', 'Retry Manager', 'Dead Letter Queue'];
  readonly requiresRealEventSources = true;

  constructor(
    context: EBusServiceContext,
    private readonly repositories: EBusRepositories,
    private readonly engineSourceRegistry: EngineSourceAdapterRegistry
  ) {
    super(context);
  }

  async deliverEvent(eventId: EventId, subscriberId: SubscriberId): Promise<DeliveryResult> {
    if (!this.engineSourceRegistry.isConfigured()) {
      return {
        success: false,
        deliveryId: null,
        error:
          'Delivery Engine Not Active — Cannot deliver events until real event sources are connected.',
        isPlaceholder: true,
        source: 'Event Bus — Delivery Engine Not Active',
      };
    }

    const delivered = await this.engineSourceRegistry
      .getActiveAdapter()
      .deliverEvent(eventId, subscriberId);

    if (delivered) {
      this.context.eventBus.emit(EBusInternalEventTypes.EVENT_DELIVERED, { eventId, subscriberId });
    }

    return {
      success: false,
      deliveryId: null,
      error:
        'Delivery Engine Not Active — Cannot deliver events until real event sources are connected.',
      isPlaceholder: true,
      source: 'Event Bus — Delivery Engine Not Active',
    };
  }

  getAllDeliveries(): RTWOEventDelivery[] {
    if (!this.engineSourceRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.deliveries.findAll();
  }

  getDelivery(deliveryId: DeliveryId): RTWOEventDelivery | null {
    if (!this.engineSourceRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.deliveries.findById(deliveryId);
  }

  getPendingDeliveryCount(): 0 {
    return 0;
  }

  getFailedDeliveryCount(): 0 {
    return 0;
  }
}
